<template>
  <div class="h-full flex flex-col min-h-0 bg-[#fafbfc] relative overflow-hidden min-w-0">
    <main class="flex-1 flex flex-col min-h-0 min-w-0">
      <header class="px-8 py-6 sticky top-0 z-30 bg-[#fafbfc]/90 backdrop-blur-md border-b border-[#1c4848]/5 shrink-0">
        <div class="max-w-[1600px] mx-auto flex items-center justify-between">
          <div>
            <div class="flex items-center gap-2 text-[#1c4848]/40 mb-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 12h4"/><path d="M8 10V6"/><path d="M15 13h.01"/><path d="M18 11h.01"/><path d="M17 16H7a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2Z"/></svg>
              <span class="text-[10px] font-bold uppercase tracking-wider">Library / Games</span>
            </div>
            <h1 class="text-2xl font-serif font-medium text-[#1c4848] tracking-tight">
              Interactive Games
            </h1>
          </div>

          <div class="flex items-center gap-4">
            <div class="relative hidden md:block">
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search games..."
                class="pl-10 pr-4 py-2.5 bg-white border border-[#1c4848]/10 rounded-xl text-sm font-medium text-[#1c4848] focus:border-[#1c4848]/30 outline-none w-64 shadow-sm"
              >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1c4848]/40"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            </div>

            <Menu as="div" class="relative">
              <MenuButton class="flex items-center gap-2 px-5 py-2.5 bg-[#1c4848] text-white rounded-xl shadow-lg shadow-[#1c4848]/20 hover:bg-[#153838] active:scale-95 transition-all">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/></svg>
                <span class="text-xs font-bold tracking-wide">Create Game</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" class="opacity-60 ml-1"><path d="M7 10l5 5 5-5z"/></svg>
              </MenuButton>
              <transition enter-active-class="transition duration-200 ease-out" enter-from-class="opacity-0 scale-95" enter-to-class="opacity-100 scale-100" leave-active-class="transition duration-100 ease-in" leave-from-class="opacity-100 scale-100" leave-to-class="opacity-0 scale-95">
                <MenuItems class="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl border border-[#1c4848]/10 p-2 z-50 origin-top-right">
                  <div class="px-3 py-2 text-[10px] font-bold text-[#1c4848]/40 uppercase tracking-wider">Select Game Type</div>
                  <MenuItem v-slot="{ active }">
                    <button type="button" class="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-colors" :class="active ? 'bg-[#fafbfc]' : ''" @click="openMemoryModal">
                      <div class="w-8 h-8 rounded-lg bg-[#e0f2f1] text-[#00695c] flex items-center justify-center">
                        <IconMemoryGame class="w-[18px] h-[18px]" />
                      </div>
                      <div>
                        <p class="text-sm font-bold text-[#1c4848]">Match Pairs</p>
                        <p class="text-[10px] text-[#1c4848]/50">Memory card game</p>
                      </div>
                    </button>
                  </MenuItem>
                  <MenuItem v-slot="{ active }">
                    <button type="button" class="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-colors" :class="active ? 'bg-[#fafbfc]' : ''" @click="openSlidingModal">
                      <div class="w-8 h-8 rounded-lg bg-[#fff3e0] text-[#e65100] flex items-center justify-center">
                        <IconSlidingPuzzle class="w-[18px] h-[18px]" />
                      </div>
                      <div>
                        <p class="text-sm font-bold text-[#1c4848]">Sliding Puzzle</p>
                        <p class="text-[10px] text-[#1c4848]/50">Classic grid puzzle</p>
                      </div>
                    </button>
                  </MenuItem>
                  <MenuItem v-slot="{ active }">
                    <button type="button" class="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-colors" :class="active ? 'bg-[#fafbfc]' : ''" @click="openCatchStarModal">
                      <div class="w-8 h-8 rounded-lg bg-[#fff8e1] text-[#f9a825] flex items-center justify-center">
                        <IconCatchStar class="w-[18px] h-[18px]" />
                      </div>
                      <div>
                        <p class="text-sm font-bold text-[#1c4848]">Catch the Star</p>
                        <p class="text-[10px] text-[#1c4848]/50">Catch stars, avoid meteors</p>
                      </div>
                    </button>
                  </MenuItem>
                  <MenuItem v-slot="{ active }">
                    <button type="button" class="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-colors" :class="active ? 'bg-[#fafbfc]' : ''" @click="openColorMatchModal">
                      <div class="w-8 h-8 rounded-lg bg-[#e8f5e9] text-[#43a047] flex items-center justify-center">
                        <IconColorMatch class="w-[18px] h-[18px]" />
                      </div>
                      <div>
                        <p class="text-sm font-bold text-[#1c4848]">Color Match</p>
                        <p class="text-[10px] text-[#1c4848]/50">Match 3+ bubbles</p>
                      </div>
                    </button>
                  </MenuItem>
                  <MenuItem v-slot="{ active }">
                    <button type="button" class="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-colors" :class="active ? 'bg-[#fafbfc]' : ''" @click="openBubbleShooterModal">
                      <div class="w-8 h-8 rounded-lg bg-[#e3f2fd] text-[#1e88e5] flex items-center justify-center">
                        <IconBubbleShooter class="w-[18px] h-[18px]" />
                      </div>
                      <div>
                        <p class="text-sm font-bold text-[#1c4848]">Bubble Shooter</p>
                        <p class="text-[10px] text-[#1c4848]/50">Shoot and clear</p>
                      </div>
                    </button>
                  </MenuItem>
                  <MenuItem v-slot="{ active }">
                    <button type="button" class="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-colors" :class="active ? 'bg-[#fafbfc]' : ''" @click="openBlockStackModal">
                      <div class="w-8 h-8 rounded-lg bg-[#efebe9] text-[#795548] flex items-center justify-center">
                        <IconBlockStack class="w-[18px] h-[18px]" />
                      </div>
                      <div>
                        <p class="text-sm font-bold text-[#1c4848]">Block Stack</p>
                        <p class="text-[10px] text-[#1c4848]/50">Stack blocks, clear rows</p>
                      </div>
                    </button>
                  </MenuItem>
                </MenuItems>
              </transition>
            </Menu>
          </div>
        </div>
      </header>

      <Suspense>
        <GamesContent v-model:search-query="searchQuery" @play="openPlayModal" @edit="openEditModal" @delete="onDelete" />
        <template #fallback>
          <div class="flex-1 min-h-0 overflow-y-auto p-8 pb-32">
            <div class="flex gap-2 pb-6 max-w-[1600px] mx-auto">
              <div class="h-9 w-24 rounded-full bg-[#1c4848]/10 animate-pulse" />
              <div class="h-9 w-20 rounded-full bg-[#1c4848]/5 animate-pulse" />
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6 max-w-[1600px] mx-auto">
              <GameCardSkeleton v-for="i in 8" :key="i" />
            </div>
          </div>
        </template>
      </Suspense>
    </main>

    <CreateMemoryGameModal v-model="showMemoryModal" @saved="onGameSaved" />
    <CreateSlidingGameModal v-model="showSlidingModal" @saved="onGameSaved" />
    <EditGameModal v-model="showEditModal" :game="editGame" @saved="onGameSaved" />
    <PlayMemoryGameModal v-model="showPlayMemoryModal" :game="playGame" />
    <PlaySlidingPuzzleModal v-model="showPlayPuzzleModal" :game="playGame" />
    <PlayCatchStarModal v-model="showPlayCatchStarModal" :game="playGame" />
    <PlayColorMatchModal v-model="showPlayColorMatchModal" :game="playGame" />
    <PlayBubbleShooterModal v-model="showPlayBubbleShooterModal" :game="playGame" />
    <PlayBlockStackModal v-model="showPlayBlockStackModal" :game="playGame" />
    <CreateCatchStarModal v-model="showCatchStarModal" @saved="onGameSaved" />
    <CreateColorMatchModal v-model="showColorMatchModal" @saved="onGameSaved" />
    <CreateBubbleShooterModal v-model="showBubbleShooterModal" @saved="onGameSaved" />
    <CreateBlockStackModal v-model="showBlockStackModal" @saved="onGameSaved" />
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue';
import GamesContent from '@/pages/GamesContent.vue';
import GameCardSkeleton from '@/components/app/dashboard/games/GameCardSkeleton.vue';
import IconMemoryGame from '@/components/app/dashboard/games/icons/IconMemoryGame.vue';
import IconSlidingPuzzle from '@/components/app/dashboard/games/icons/IconSlidingPuzzle.vue';
import IconCatchStar from '@/components/app/dashboard/games/icons/IconCatchStar.vue';
import IconColorMatch from '@/components/app/dashboard/games/icons/IconColorMatch.vue';
import IconBubbleShooter from '@/components/app/dashboard/games/icons/IconBubbleShooter.vue';
import IconBlockStack from '@/components/app/dashboard/games/icons/IconBlockStack.vue';
import CreateMemoryGameModal from '@/components/app/dashboard/modals/CreateMemoryGameModal.vue';
import CreateSlidingGameModal from '@/components/app/dashboard/modals/CreateSlidingGameModal.vue';
import CreateCatchStarModal from '@/components/app/dashboard/modals/CreateCatchStarModal.vue';
import CreateColorMatchModal from '@/components/app/dashboard/modals/CreateColorMatchModal.vue';
import CreateBubbleShooterModal from '@/components/app/dashboard/modals/CreateBubbleShooterModal.vue';
import CreateBlockStackModal from '@/components/app/dashboard/modals/CreateBlockStackModal.vue';
import EditGameModal from '@/components/app/dashboard/modals/EditGameModal.vue';
import PlayMemoryGameModal from '@/components/app/dashboard/modals/PlayMemoryGameModal.vue';
import PlaySlidingPuzzleModal from '@/components/app/dashboard/modals/PlaySlidingPuzzleModal.vue';
import PlayCatchStarModal from '@/components/app/dashboard/modals/PlayCatchStarModal.vue';
import PlayColorMatchModal from '@/components/app/dashboard/modals/PlayColorMatchModal.vue';
import PlayBubbleShooterModal from '@/components/app/dashboard/modals/PlayBubbleShooterModal.vue';
import PlayBlockStackModal from '@/components/app/dashboard/modals/PlayBlockStackModal.vue';
import { useGamesStore } from '@/store/games/index.js';

const gamesStore = useGamesStore();
const searchQuery = ref('');
const showMemoryModal = ref(false);
const showSlidingModal = ref(false);
const showEditModal = ref(false);
const editGame = ref(null);
const showPlayMemoryModal = ref(false);
const showPlayPuzzleModal = ref(false);
const showPlayCatchStarModal = ref(false);
const showPlayColorMatchModal = ref(false);
const showPlayBubbleShooterModal = ref(false);
const showPlayBlockStackModal = ref(false);
const showCatchStarModal = ref(false);
const showColorMatchModal = ref(false);
const showBubbleShooterModal = ref(false);
const showBlockStackModal = ref(false);
const playGame = ref(null);

function openMemoryModal() {
  showMemoryModal.value = true;
}

function openSlidingModal() {
  showSlidingModal.value = true;
}

function openCatchStarModal() {
  showCatchStarModal.value = true;
}

function openColorMatchModal() {
  showColorMatchModal.value = true;
}

function openBubbleShooterModal() {
  showBubbleShooterModal.value = true;
}

function openBlockStackModal() {
  showBlockStackModal.value = true;
}

function openEditModal(game) {
  editGame.value = game;
  showEditModal.value = true;
}

function openPlayModal(game) {
  playGame.value = game;
  if (game.game_type === 'match_pairs') showPlayMemoryModal.value = true;
  else if (game.game_type === 'sliding_puzzle') showPlayPuzzleModal.value = true;
  else if (game.game_type === 'catch_star') showPlayCatchStarModal.value = true;
  else if (game.game_type === 'color_match') showPlayColorMatchModal.value = true;
  else if (game.game_type === 'bubble_shooter') showPlayBubbleShooterModal.value = true;
  else if (game.game_type === 'block_stack') showPlayBlockStackModal.value = true;
}

async function onDelete(game) {
  if (!confirm(`Delete "${game.name}"?`)) return;
  await gamesStore.delete(game.id);
}

function onGameSaved() {
  gamesStore.fetchList();
}
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(28, 72, 72, 0.1);
  border-radius: 20px;
}
.custom-scrollbar:hover::-webkit-scrollbar-thumb {
  background-color: rgba(28, 72, 72, 0.2);
}
</style>
